import type { SxProps, Theme } from '@mui/material'

export type BubbleLevel = 'outer' | 'middle' | 'inner'

/**
 * Mirrors the Events session-tree alpha layering
 * (src/features/events/conversation/tree/defaults/sessionTreeDefaults.ts) so the
 * inbox popover reads as the same concentric, progressively-lighter bubble system.
 *
 * The tree tints with rgba(255,255,255,a) in dark mode and rgba(62,39,35,a) in
 * light mode, lightening by depth: host 0.04, project 0.025, session 0.02.
 * Padding/gaps are tightened here because the popover is far narrower than the
 * full-width Events page and three nesting levels would otherwise overflow.
 */
const BUBBLE_ALPHA_BY_LEVEL: Readonly<Record<BubbleLevel, number>> = {
  outer: 0.04,
  middle: 0.025,
  inner: 0.02,
}

const LABEL_OPACITY_BY_LEVEL: Readonly<Record<BubbleLevel, number>> = {
  outer: 0.3,
  middle: 0.5,
  inner: 0.7,
}

const BUBBLE_PADDING_BY_LEVEL: Readonly<Record<BubbleLevel, number>> = {
  outer: 1.25,
  middle: 1,
  inner: 1,
}

const BUBBLE_GAP_BY_LEVEL: Readonly<Record<BubbleLevel, number>> = {
  outer: 1,
  middle: 0.75,
  inner: 0.5,
}

const BUBBLE_MARGIN_BOTTOM_BY_LEVEL: Readonly<Record<BubbleLevel, number>> = {
  outer: 1.25,
  middle: 1,
  inner: 0.75,
}

function getBubbleTint(theme: Theme, alpha: number): string {
  return theme.palette.mode === 'dark'
    ? `rgba(255, 255, 255, ${alpha})`
    : `rgba(62, 39, 35, ${alpha})`
}

export function getBubbleSx(level: BubbleLevel, theme: Theme): SxProps<Theme> {
  return {
    display: 'flex',
    flexDirection: 'column',
    gap: BUBBLE_GAP_BY_LEVEL[level],
    p: BUBBLE_PADDING_BY_LEVEL[level],
    borderRadius: 1,
    backgroundColor: getBubbleTint(theme, BUBBLE_ALPHA_BY_LEVEL[level]),
    '&:last-of-type': { mb: 0 },
    mb: BUBBLE_MARGIN_BOTTOM_BY_LEVEL[level],
  }
}

export function getBubbleLabelSx(level: BubbleLevel, theme: Theme): SxProps<Theme> {
  return {
    color: getBubbleTint(theme, LABEL_OPACITY_BY_LEVEL[level]),
    fontWeight: level === 'inner' ? 600 : 500,
  }
}
